import { use, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { MultiPageDialog } from '~components';
import SelectedDevicesContext from '~features/devices/providers/selectedDevicesContext';
import { SelectOrganisationAndResourceGroupForm } from '~features/keys';
import Organisation from '~interfaces/organisation';
import ResourceGroup from '~interfaces/resourceGroup';

import SelectedDevicesOverview from '../forms/selectedDevicesOverview';

interface Props {
	open: boolean;
	loading?: boolean;
	onClose: () => void;
	onFinish: (
		organisationId: string,
		resourceGroupId: string | undefined,
		closeDialog: () => void
	) => void;
	currentlyAssignedOrganisation?: Organisation | undefined;
	showEditOrganisation?: boolean;
}

const DeviceMultiAssignDialog = ({
	open,
	loading = false,
	onClose,
	onFinish,
	currentlyAssignedOrganisation,
	showEditOrganisation = true,
}: Props) => {
	const { t } = useTranslation();
	const selectedDevicesContext = use(SelectedDevicesContext);

	const [selectedOrganisation, setSelectedOrganisation] = useState<Organisation | undefined>(
		undefined
	);
	const [selectedResourceGroup, setSelectedResourceGroup] = useState<ResourceGroup | undefined>(
		undefined
	);
	const effectiveOrganisation =
		showEditOrganisation ? selectedOrganisation : currentlyAssignedOrganisation;
	const isChangeResourceGroup = !showEditOrganisation;

	const subtitle = useMemo(() => {
		const entries = [];
		if (effectiveOrganisation) entries.push(effectiveOrganisation.name);
		if (selectedResourceGroup) entries.push(selectedResourceGroup.name);
		return entries.join(' > ');
	}, [effectiveOrganisation, selectedResourceGroup]);

	const reset = () => {
		setSelectedOrganisation(undefined);
		setSelectedResourceGroup(undefined);
	};

	const title =
		isChangeResourceGroup ? t('changeResourceGroup', { ns: 'actions' })
		: selectedDevicesContext.singleDevice ? t('device.labels.assigDevice', { ns: 'entities' })
		: t('device.labels.multiAssignDevices', { ns: 'entities' });

	return (
		<MultiPageDialog
			title={title}
			open={open}
			loading={loading}
			subtitle={subtitle}
			onClose={() => {
				onClose();
				reset();
			}}
			onFinish={() => {
				if (effectiveOrganisation) {
					onFinish(effectiveOrganisation.id, selectedResourceGroup?.id, () => {
						reset();
					});
				}
			}}
			pages={[
				{
					key: 'assignForm',
					content: (
						<SelectOrganisationAndResourceGroupForm
							organisation={effectiveOrganisation}
							showEditOrganisation={showEditOrganisation}
							resourceGroup={selectedResourceGroup}
							resourceGroupDisabled={effectiveOrganisation === undefined}
							onValuesChanged={(newValues) => {
								setSelectedOrganisation(newValues.organisation);
								setSelectedResourceGroup(newValues.resourceGroup);
							}}
						/>
					),
					enableNext: effectiveOrganisation !== undefined,
				},
				{
					key: 'selectedDevices',
					content: (
						<SelectedDevicesOverview
							selectedDevices={
								selectedDevicesContext.singleDevice ?
									[selectedDevicesContext.singleDevice]
								:	selectedDevicesContext.selectedDevices
							}
							onRemoveClicked={selectedDevicesContext.removeDevice}
						/>
					),
				},
			]}
		/>
	);
};

export default DeviceMultiAssignDialog;
